/**
 * ConnectionCreatorDiagram - Full-featured diagram for Connection Creator
 * Supports loading from JSON, saving to JSON, and full interactivity
 */

import {
    ArrowLeftOutlined,
    ArrowRightOutlined,
    CodeOutlined,
    DeleteOutlined,
    LoadingOutlined,
    PlusOutlined,
    SaveOutlined,
    SettingOutlined,
    ZoomInOutlined,
    ZoomOutOutlined,
} from "@ant-design/icons";
import { useBlocker, useSearch } from "@tanstack/react-router";
import {
    applyEdgeChanges,
    applyNodeChanges,
    Background,
    BackgroundVariant,
    Connection,
    Edge,
    MarkerType,
    Node,
    Panel,
    ReactFlow,
    reconnectEdge,
    useReactFlow,
    useViewport,
} from "@xyflow/react";
import { Button, Dropdown, InputNumber, message, Modal } from "antd";
import { useCCEntityOptional } from "components/CC/context/CCEntityContext.tsx";
import { CCStartNodesProvider } from "components/CC/context/CCStartNodesContext.tsx";
import { DiagramNodesProvider, DiagramNodeOption } from "components/CC/context/DiagramNodesContext.tsx";
import { ConditionDrawer } from "components/CC/Edges/ConditionEdge/Drawers/ConditionDrawer.tsx";
import { BreakerDrawer } from "components/CC/Nodes/BreakerNode/Drawers/BreakerDrawer.tsx";
import { EndDrawer } from "components/CC/Nodes/EndNode/Drawers/EndDrawer.tsx";
import { HttpDrawer } from "components/CC/Nodes/HttpNode/Drawers/HttpDrawer.tsx";
import { HttpMethodDrawer } from "components/CC/Nodes/MethodNodes/Drawers/HttpMethodDrawer.tsx";
import { MethodsNode } from "components/CC/Nodes/MethodNodes/MethodsNode.tsx";
import { MirrorDrawer } from "components/CC/Nodes/MirrorNode/Drawers/MirrorDrawer.tsx";
import { ModifyFieldDrawer } from "components/CC/Nodes/ModifyFieldNode/Drawers/ModifyFieldDrawer.tsx";
import { getMenuNodesByCategory } from "components/CC/Nodes/node-config.ts";
import { NodePopover } from "components/CC/Nodes/NodeBase/NodePopover.tsx";
import { StartIcon } from "components/CC/Nodes/NodeIcons";
import { standardNodeTypes } from "components/CC/Nodes/nodes.tsx";
import { RepeaterDrawer } from "components/CC/Nodes/RepeaterNode/Drawers/RepeaterDrawer.tsx";
import { SetDrawer } from "components/CC/Nodes/SetNode/Drawers/SetDrawer.tsx";
import { StartDrawer } from "components/CC/Nodes/StartNode/Drawers/StartDrawer.tsx";
import { SubScenarioDrawer } from "components/CC/Nodes/SubScenarioNode/Drawers/SubScenarioDrawer.tsx";
import { WaitDrawer } from "components/CC/Nodes/WaitNode/Drawers/WaitDrawer.tsx";
import { WebhookDrawer } from "components/CC/Nodes/WebhookNode/Drawers/WebhookDrawer.tsx";
import { CC_ENTITY_CONFIGS, getAvailableFlowTypesForNewStart } from "components/CC/types/entityConfig.ts";
import { NodeType, StartFlowType } from "components/CC/types/enums.ts";
import { DrawerId } from "components/CC/types/models.ts";
import { BlueprintDrawer } from "components/CC/UI/BlueprintDrawer.tsx";
import { CustomEdge, type CustomEdgeData } from "components/CC/UI/CustomEdge.tsx";
import { NotesDrawer } from "components/CC/UI/NotesDrawer.tsx";
import {
    findEdgeBySelectionId,
    findNodeBySelectionId,
    getCCDrawerSelectionParam,
    getEdgeSelectionId,
    getNodeSelectionId,
    isCCDrawerId,
    isCCNodeDrawerId,
} from "components/CC/utils/drawerUrlState.ts";
import { getDefaultFormData, NodeFormData } from "components/CC/utils/nodeDefaults.ts";
import { useDrawer } from "hooks/useDrawer.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import React, {
    Fragment,
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";
import { useRecoilState } from "recoil";

import { ccEdgesAtom, ccNodesAtom, ccSelectedEdgeAtom, ccSelectedNodeAtom } from "../../atoms/diagramAtom";
import { useDiagramHistory } from "../../hooks/useDiagramHistory";
import { calculateSortOrder } from "../../utils/calculateSortOrder";
import { getFlowNumber, getNextFlowPositionId } from "../../utils/flowPositionIds";

import type { MenuProps } from "antd";

import "@xyflow/react/dist/style.css";

// Node types configuration
const nodeTypes = {
    ...standardNodeTypes,
    [NodeType.Methods]: MethodsNode,
};

const edgeTypes = {
    customEdge: CustomEdge,
};

// Node type to drawer mapping
const NODE_DRAWER_MAP: Partial<Record<string, DrawerId>> = {
    // Start node (badge determined by formData.flow)
    startNode: DrawerId.Start,
    endNode: DrawerId.End,
    // Time/Control nodes
    waitNode: DrawerId.Wait,
    repeaterNode: DrawerId.Repeater,
    breakerNode: DrawerId.Breaker,
    // Data nodes
    modifyFieldNode: DrawerId.ModifyField,
    setNode: DrawerId.Set,
    mirrorNode: DrawerId.Mirror,
    // HTTP nodes
    httpNode: DrawerId.Http,
    methodsNode: DrawerId.HttpMethod,
    // Communication nodes
    webhookNode: DrawerId.Webhook,
    subScenarioNode: DrawerId.SubScenario,
};

// Start node menu configuration with icons
const START_NODE_MENU_CONFIG: { type: StartFlowType; labelKey: string; color: string; icon: React.FC }[] = [
    { type: "post", labelKey: "enums.flow_types.post", color: "#008080", icon: () => <StartIcon /> },
    { type: "ping", labelKey: "enums.flow_types.ping", color: "#008080", icon: () => <StartIcon /> },
    { type: "postVerify", labelKey: "enums.flow_types.postVerify", color: "#008080", icon: () => <StartIcon /> },
    { type: "webhook", labelKey: "enums.flow_types.webhook", color: "#008080", icon: () => <StartIcon /> },
    { type: "instant", labelKey: "enums.flow_types.instant", color: "#008080", icon: () => <StartIcon /> },
];

export interface DiagramNode {
    id: string;
    type: string;
    position: { x: number; y: number };
    data?: Record<string, unknown>;
}

export interface DiagramEdge {
    id: string;
    source: string;
    target: string;
    type?: string;
    data?: Record<string, unknown>;
}

export interface DiagramState {
    nodes: DiagramNode[];
    edges: DiagramEdge[];
    notes?: string;
}

const sanitizeDiagramEdgeData = (data: Edge["data"]): Record<string, unknown> | undefined => {
    if (!data || typeof data !== "object") return undefined;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { onWrenchClick, onDelete, showWrench, isHovered, ...cleanData } = data as Record<string, unknown>;
    return Object.keys(cleanData).length > 0 ? cleanData : undefined;
};

const toCleanDiagramEdge = (edge: Pick<Edge, "id" | "source" | "target" | "type" | "data">): DiagramEdge => {
    const result: DiagramEdge = {
        id: edge.id,
        source: edge.source,
        target: edge.target,
        type: edge.type,
    };
    const cleanData = sanitizeDiagramEdgeData(edge.data);
    if (cleanData) {
        result.data = cleanData;
    }
    return result;
};

export interface ConnectionCreatorDiagramRef {
    getState: () => DiagramState;
    setNodes: (nodes: DiagramNode[]) => void;
    setEdges: (edges: DiagramEdge[]) => void;
    // History methods
    undo: () => void;
    redo: () => void;
    canUndo: boolean;
    canRedo: boolean;
    // Save state
    isDirty: boolean;
    markAsSaved: () => void;
}

export interface ConnectionCreatorDiagramProps {
    initialNodes?: DiagramNode[];
    initialEdges?: DiagramEdge[];
    initialNotes?: string;
    showControls?: boolean;
    height?: number | string;
    onSave?: () => void;
    isSaving?: boolean;
}

interface SearchParams {
    drawerIds?: string;
    [key: string]: unknown;
}

// Helper to convert diagram edges to ReactFlow edges
const toReactFlowEdges = (inputEdges: DiagramEdge[]): Edge[] => {
    return inputEdges.map((edge) => ({
        ...edge,
        type: edge.type ?? "customEdge",
        markerEnd: { type: MarkerType.ArrowClosed, color: "#0706254D" },
        data: edge.data,
    })) as Edge[];
};

// Helper to convert diagram nodes to ReactFlow nodes (without handlers - those are added later)
const toReactFlowNodes = (inputNodes: DiagramNode[]): Node[] => {
    return inputNodes.map((node) => ({
        ...node,
        data: node.data ?? {},
    })) as Node[];
};

// Add node dialog state type (for Plus button clicks)
interface AddNodeDialogState {
    open: boolean;
    sourceNodeId: string | null;
    side: "left" | "right" | null;
}

// Canvas context menu state type (for right-click on empty space)
interface CanvasContextMenuState {
    open: boolean;
    /** Flow coordinates where the node will be placed */
    flowPosition: { x: number; y: number } | null;
    /** Screen coordinates where the menu will be displayed */
    screenPosition: { x: number; y: number } | null;
}

// Edge context menu state type (for right-click on edge/arrow)
interface EdgeContextMenuState {
    open: boolean;
    edgeId: string | null;
    screenPosition: { x: number; y: number } | null;
}

// Inner component - uses controlled state
const DiagramContent = forwardRef<ConnectionCreatorDiagramRef, ConnectionCreatorDiagramProps>(
    (
        {
            initialNodes = [],
            initialEdges = [],
            initialNotes = "",
            showControls = true,
            height = "100%",
            onSave,
            isSaving = false,
        },
        ref
    ) => {
        const drawerUtils = useDrawer();
        const { show: showDrawer } = drawerUtils;
        const { t } = usePaldockTranslation("components.connection-creator");
        const ccEntity = useCCEntityOptional();
        const searchParams = useSearch({ strict: false }) as SearchParams;
        const [selectedNode, setSelectedNode] = useRecoilState(ccSelectedNodeAtom);
        const [selectedEdge, setSelectedEdge] = useRecoilState(ccSelectedEdgeAtom);

        // State for nodes and edges using Recoil atoms
        const [nodes, setNodes] = useRecoilState(ccNodesAtom);
        const [edges, setEdges] = useRecoilState(ccEdgesAtom);

        // History tracking - must be before cleanup effect that uses resetHistory
        const {
            pushState,
            undo,
            redo,
            currentState,
            canUndo,
            canRedo,
            isDirty,
            markAsSaved,
            reset: resetHistory,
        } = useDiagramHistory();

        // Initialize atoms from props on mount only (not on prop changes - we want to keep local changes)
        const isInitializedFromPropsRef = useRef(false);
        useEffect(() => {
            if (!isInitializedFromPropsRef.current) {
                setNodes(toReactFlowNodes(initialNodes));
                setEdges(toReactFlowEdges(initialEdges));
                isInitializedFromPropsRef.current = true;
            }
            // Note: We intentionally don't react to initialNodes/initialEdges changes
            // to preserve local diagram state (e.g., after failed save)
        }, [setNodes, setEdges]);

        // Cleanup: reset atoms when diagram unmounts to prevent stale state
        useEffect(() => {
            return () => {
                setNodes([]);
                setEdges([]);
                setSelectedNode(null);
                setSelectedEdge(null);
                resetHistory();
            };
        }, [setNodes, setEdges, setSelectedNode, setSelectedEdge, resetHistory]);
        const [notesContent, setNotesContent] = useState(initialNotes);

        // Refs to keep track of latest state for use in callbacks
        const edgesRef = useRef(edges);
        const nodesRef = useRef(nodes);
        useEffect(() => {
            edgesRef.current = edges;
            nodesRef.current = nodes;
        }, [edges, nodes]);
        const isRestoringRef = useRef(false);
        const isApplyingBlueprintRef = useRef(false);
        const isInitializedRef = useRef(false);
        const restoredMissingSelectionRef = useRef<string | null>(null);

        useEffect(() => {
            if (!isInitializedFromPropsRef.current) return;
            if (initialNodes.length > 0 && nodes.length === 0) return;

            const activeNodeDrawerId = drawerUtils
                .list()
                .find((drawerId): drawerId is DrawerId => isCCDrawerId(drawerId) && isCCNodeDrawerId(drawerId));

            if (activeNodeDrawerId) {
                const selectionParam = getCCDrawerSelectionParam(activeNodeDrawerId);
                const selectionId = searchParams[selectionParam];

                if (typeof selectionId === "string" && selectionId.length > 0) {
                    const node = findNodeBySelectionId(nodes, selectionId);

                    if (node) {
                        if (selectedEdge) {
                            setSelectedEdge(null);
                        }
                        if (selectedNode?.id !== node.id) {
                            setSelectedNode({
                                id: node.id,
                                type: node.type || "",
                                formData: (node.data as Record<string, unknown> | undefined)?.formData as
                                    | NodeFormData
                                    | undefined,
                            });
                        }
                        restoredMissingSelectionRef.current = null;
                    } else if (!isRestoringRef.current && !isApplyingBlueprintRef.current) {
                        const missingKey = `${activeNodeDrawerId}:${selectionId}`;
                        if (restoredMissingSelectionRef.current !== missingKey) {
                            restoredMissingSelectionRef.current = missingKey;
                            message.warning(t("diagrams.restored_step_missing"));
                        }
                        setSelectedNode(null);
                        drawerUtils.hide(activeNodeDrawerId, [selectionParam]);
                    }
                }
            }

            const conditionSelectionParam = getCCDrawerSelectionParam(DrawerId.Condition);
            const conditionSelectionId = searchParams[conditionSelectionParam];

            if (
                drawerUtils.isDrawerActive(DrawerId.Condition) &&
                typeof conditionSelectionId === "string" &&
                conditionSelectionId.length > 0
            ) {
                const edge = findEdgeBySelectionId(edges, conditionSelectionId);

                if (edge) {
                    if (selectedNode) {
                        setSelectedNode(null);
                    }
                    if (selectedEdge?.id !== edge.id) {
                        setSelectedEdge({
                            id: edge.id,
                            source: edge.source,
                            target: edge.target,
                            data: edge.data as Record<string, unknown> | undefined,
                        });
                    }
                    restoredMissingSelectionRef.current = null;
                } else if (!isRestoringRef.current) {
                    const missingKey = `${DrawerId.Condition}:${conditionSelectionId}`;
                    if (restoredMissingSelectionRef.current !== missingKey) {
                        restoredMissingSelectionRef.current = missingKey;
                        message.warning(t("diagrams.restored_step_missing"));
                    }
                    setSelectedEdge(null);
                    drawerUtils.hide(DrawerId.Condition, [conditionSelectionParam]);
                }
            }
        }, [
            drawerUtils,
            edges,
            initialNodes.length,
            nodes,
            searchParams,
            selectedEdge,
            selectedNode,
            setSelectedEdge,
            setSelectedNode,
            t,
        ]);

        // Keep isDirty in a ref to avoid stale closures in useBlocker
        const isDirtyRef = useRef(isDirty);
        isDirtyRef.current = isDirty;

        // TanStack Router navigation blocking for unsaved changes
        // Only block actual route changes, not query param changes (drawer opens)
        // Use ref to avoid stale closure - isDirty value is captured when shouldBlockFn is created
        const { proceed, reset, status } = useBlocker({
            shouldBlockFn: ({ current, next }) => {
                // Don't block if just query params changed (drawer open/close)
                if (current.pathname === next.pathname) {
                    return false;
                }
                return isDirtyRef.current;
            },
            enableBeforeUnload: () => isDirtyRef.current,
            withResolver: true,
        });

        // Show confirmation modal when navigation is blocked
        const modalRef = useRef<ReturnType<typeof Modal.confirm> | null>(null);

        useEffect(() => {
            if (status === "blocked") {
                // Safety check: if not actually dirty, just proceed without showing modal
                if (!isDirtyRef.current) {
                    proceed();
                    return;
                }

                // Destroy any existing modal first
                if (modalRef.current) {
                    modalRef.current.destroy();
                }

                modalRef.current = Modal.confirm({
                    title: t("diagrams.unsaved_changes"),
                    content: t("diagrams.unsaved_changes_message"),
                    okText: t("diagrams.leave"),
                    cancelText: t("diagrams.stay"),
                    okType: "danger",
                    onOk: () => {
                        modalRef.current = null;
                        proceed();
                    },
                    onCancel: () => {
                        modalRef.current = null;
                        reset();
                    },
                });
            }

            // Cleanup on unmount
            return () => {
                if (modalRef.current) {
                    modalRef.current.destroy();
                    modalRef.current = null;
                }
            };
        }, [status, proceed, reset, t]);

        // Track changes and push to history (debounced to avoid too many snapshots during drag)
        const pushTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
        const lastPushedStateRef = useRef<string>("");

        // Push initial state to history on mount and mark as saved (clean state)
        useEffect(() => {
            // Wait for Recoil state to be populated from props before initializing history
            // For non-empty initial data, wait until Recoil atoms are populated
            // For empty initial data (empty scenario), initialize immediately after first render
            const hasInitialData = initialNodes.length > 0 || initialEdges.length > 0;
            const recoilPopulated = nodes.length > 0 || edges.length > 0;

            // Initialize when: (a) we have initial data and Recoil is populated, OR (b) initial data is empty
            // and props init ref indicates we've already set atoms (meaning Recoil reflects the empty state)
            if (
                !isInitializedRef.current &&
                ((!hasInitialData && isInitializedFromPropsRef.current) || (hasInitialData && recoilPopulated))
            ) {
                // Build clean state matching what change tracking would produce
                const cleanNodes: DiagramNode[] = nodes.map((node) => ({
                    id: node.id,
                    type: node.type ?? "unknown",
                    position: node.position,
                    data: node.data as Record<string, unknown>,
                }));
                const cleanEdges: DiagramEdge[] = edges.map(toCleanDiagramEdge);

                // Set lastPushedStateRef to prevent change tracking from pushing duplicate state
                const stateKey = JSON.stringify({
                    nodes: cleanNodes,
                    edges: cleanEdges,
                    notes: initialNotes || undefined,
                });
                lastPushedStateRef.current = stateKey;

                pushState({ nodes: cleanNodes, edges: cleanEdges, notes: initialNotes || undefined });
                markAsSaved(); // Initial state is "saved" - no unsaved changes on load
                isInitializedRef.current = true;
            }
        }, [initialNodes, initialEdges, initialNotes, nodes, edges, pushState, markAsSaved]);

        useEffect(() => {
            // Skip if we're restoring from history
            if (isRestoringRef.current) {
                return;
            }

            // Skip if not initialized yet - let the init effect handle initial state
            if (!isInitializedRef.current) {
                return;
            }

            // Clear pending timeout
            if (pushTimeoutRef.current) {
                clearTimeout(pushTimeoutRef.current);
            }

            // Debounce the push to avoid too many snapshots during drag operations
            pushTimeoutRef.current = setTimeout(() => {
                const cleanNodes: DiagramNode[] = nodes.map((node) => ({
                    id: node.id,
                    type: node.type ?? "unknown",
                    position: node.position,
                    data: node.data as Record<string, unknown>,
                }));
                const cleanEdges: DiagramEdge[] = edges.map(toCleanDiagramEdge);

                // Check if state actually changed
                const stateKey = JSON.stringify({
                    nodes: cleanNodes,
                    edges: cleanEdges,
                    notes: notesContent || undefined,
                });
                if (stateKey !== lastPushedStateRef.current) {
                    lastPushedStateRef.current = stateKey;
                    pushState({ nodes: cleanNodes, edges: cleanEdges, notes: notesContent || undefined });
                }
            }, 300);

            return () => {
                if (pushTimeoutRef.current) {
                    clearTimeout(pushTimeoutRef.current);
                }
            };
        }, [nodes, edges, notesContent, pushState]);

        // Handle undo action
        const handleUndo = useCallback(() => {
            if (!canUndo) return;
            isRestoringRef.current = true;
            undo();
        }, [canUndo, undo]);

        // Handle redo action
        const handleRedo = useCallback(() => {
            if (!canRedo) return;
            isRestoringRef.current = true;
            redo();
        }, [canRedo, redo]);

        // Restore state when currentState changes (from undo/redo)
        useEffect(() => {
            if (currentState && isRestoringRef.current) {
                setNodes(toReactFlowNodes(currentState.nodes));
                setEdges(toReactFlowEdges(currentState.edges));
                setNotesContent(currentState.notes || "");
                // Reset the last pushed state to match restored state
                lastPushedStateRef.current = JSON.stringify(currentState);
                // Allow future changes to be tracked after a short delay
                setTimeout(() => {
                    isRestoringRef.current = false;
                }, 50);
            }
        }, [currentState]);

        // Keyboard shortcuts for undo/redo
        useEffect(() => {
            const handleKeyDown = (e: KeyboardEvent) => {
                if ((e.metaKey || e.ctrlKey) && e.key === "z") {
                    if (e.shiftKey) {
                        e.preventDefault();
                        handleRedo();
                    } else {
                        e.preventDefault();
                        handleUndo();
                    }
                }
                if ((e.metaKey || e.ctrlKey) && e.key === "y") {
                    e.preventDefault();
                    handleRedo();
                }
            };

            window.addEventListener("keydown", handleKeyDown);
            return () => window.removeEventListener("keydown", handleKeyDown);
        }, [handleUndo, handleRedo]);

        // State for Add Node Dialog (when clicking Plus on nodes)
        const [addNodeDialogState, setAddNodeDialogState] = useState<AddNodeDialogState>({
            open: false,
            sourceNodeId: null,
            side: null,
        });

        // State for Notes Drawer
        const [notesDrawerOpen, setNotesDrawerOpen] = useState(false);

        // State for Blueprint Drawer
        const [blueprintDrawerOpen, setBlueprintDrawerOpen] = useState(false);

        // State for Canvas Context Menu (right-click on empty space)
        const [canvasMenuState, setCanvasMenuState] = useState<CanvasContextMenuState>({
            open: false,
            flowPosition: null,
            screenPosition: null,
        });

        // State for Edge Context Menu (right-click on edge/arrow)
        const [edgeMenuState, setEdgeMenuState] = useState<EdgeContextMenuState>({
            open: false,
            edgeId: null,
            screenPosition: null,
        });

        // Currently hovered edge — highlights both the edge line and its wrench icon together
        const [hoveredEdgeId, setHoveredEdgeId] = useState<string | null>(null);

        // Get ReactFlow instance for viewport info
        const reactFlowInstance = useReactFlow();
        const viewport = useViewport();
        const containerRef = useRef<HTMLDivElement>(null);

        // Zoom handlers
        const handleZoomIn = useCallback(() => {
            reactFlowInstance.zoomIn();
        }, [reactFlowInstance]);

        const handleZoomOut = useCallback(() => {
            reactFlowInstance.zoomOut();
        }, [reactFlowInstance]);

        const handleZoomChange = useCallback(
            (value: number | null) => {
                if (value !== null) {
                    const { x, y } = reactFlowInstance.getViewport();
                    reactFlowInstance.setViewport({ x, y, zoom: value / 100 });
                }
            },
            [reactFlowInstance]
        );

        // Compute existing flow types from start nodes
        const existingFlowTypes = useMemo((): StartFlowType[] => {
            return nodes
                .filter((n) => n.type === NodeType.Start)
                .map((n) => (n.data as Record<string, unknown>)?.formData as { flow?: StartFlowType } | undefined)
                .map((formData) => formData?.flow)
                .filter((flow): flow is StartFlowType => !!flow);
        }, [nodes]);

        // Compute available flow types for new start based on entity config
        const availableStartFlowTypes = useMemo((): StartFlowType[] => {
            if (!ccEntity) {
                // No entity context - allow all types (backwards compatibility)
                return ["post", "ping", "webhook", "instant"];
            }
            return getAvailableFlowTypesForNewStart(ccEntity.entityType, existingFlowTypes);
        }, [ccEntity, existingFlowTypes]);

        // Transform a methodsNode into a specific node type (no auto-creation of continuation nodes)
        const handleTransformNode = useCallback((nodeId: string, newType: NodeType) => {
            setNodes((currentNodes) => {
                return currentNodes.map((node) =>
                    node.id === nodeId
                        ? { ...node, type: newType, data: { formData: getDefaultFormData(newType) } }
                        : node
                );
            });
        }, []);

        // Open add node dialog when Plus button is clicked
        const handleOpenAddNodeDialog = useCallback((nodeId: string, side: "left" | "right") => {
            setAddNodeDialogState({
                open: true,
                sourceNodeId: nodeId,
                side,
            });
        }, []);

        // Close add node dialog
        const handleCloseAddNodeDialog = useCallback(() => {
            setAddNodeDialogState({
                open: false,
                sourceNodeId: null,
                side: null,
            });
        }, []);

        // Open canvas context menu when right-clicking on empty space
        const handlePaneContextMenu = useCallback(
            (event: MouseEvent | React.MouseEvent) => {
                event.preventDefault();
                // Convert screen coordinates to flow coordinates for node placement
                const flowPosition = reactFlowInstance.screenToFlowPosition({
                    x: event.clientX,
                    y: event.clientY,
                });
                // Store screen coordinates for menu positioning
                setCanvasMenuState({
                    open: true,
                    flowPosition,
                    screenPosition: { x: event.clientX, y: event.clientY },
                });
            },
            [reactFlowInstance]
        );

        // Close canvas context menu
        const handleCloseCanvasMenu = useCallback(() => {
            setCanvasMenuState({
                open: false,
                flowPosition: null,
                screenPosition: null,
            });
        }, []);

        // Open unified node menu centered in viewport (for toolbar plus button)
        const handleOpenToolbarMenu = useCallback(() => {
            const rect = containerRef.current?.getBoundingClientRect();
            const centerX = rect ? rect.left + rect.width / 2 : window.innerWidth / 2;
            const centerY = rect ? rect.top + rect.height / 2 : window.innerHeight / 2;
            const flowPosition = reactFlowInstance.screenToFlowPosition({ x: centerX, y: centerY });
            setCanvasMenuState({
                open: true,
                flowPosition,
                screenPosition: { x: centerX, y: centerY },
            });
        }, [reactFlowInstance]);

        // Handle start node type selection (flowType is "post", "ping", "webhook", or "instant")
        const handleStartNodeTypeSelect = useCallback(
            (flowType: StartFlowType) => {
                // If there are existing start nodes, stack the new one below the bottommost one
                const existingStartNodes = nodes.filter((n) => n.type === NodeType.Start);
                let defaultX: number;
                let defaultY: number;

                if (existingStartNodes.length > 0) {
                    const bottommost = existingStartNodes.reduce((b, n) => (n.position.y > b.position.y ? n : b));
                    defaultX = bottommost.position.x;
                    defaultY = bottommost.position.y + 120;
                } else {
                    // No existing nodes — use the menu position (viewport center for toolbar plus, click position for right-click)
                    const vp = reactFlowInstance.getViewport();
                    defaultX = canvasMenuState.flowPosition ? canvasMenuState.flowPosition.x : (-vp.x + 100) / vp.zoom;
                    defaultY = canvasMenuState.flowPosition ? canvasMenuState.flowPosition.y : (-vp.y + 200) / vp.zoom;
                }

                // Calculate new flow number (max existing flow number + 1 to avoid ID collisions after deletions)
                const existingFlowNumbers = nodes
                    .filter((n) => n.type === NodeType.Start)
                    .map((n) => getFlowNumber(n.id))
                    .filter((n) => !Number.isNaN(n));
                const newFlowNumber = existingFlowNumbers.length > 0 ? Math.max(...existingFlowNumbers) + 1 : 1;
                const startNodeId = `${newFlowNumber}-1`;
                const methodsNodeId = `${newFlowNumber}-2`;

                // Create start node with flow type and entity-specific beType in formData
                const startNode: Node = {
                    id: startNodeId,
                    type: NodeType.Start,
                    position: { x: defaultX, y: defaultY },
                    data: {
                        formData: {
                            flow: flowType,
                            beType: ccEntity ? CC_ENTITY_CONFIGS[ccEntity.entityType].startBeType : undefined,
                        },
                    },
                };

                // Create empty methodsNode connected to start node
                const methodsNode: Node = {
                    id: methodsNodeId,
                    type: NodeType.Methods,
                    position: { x: defaultX + 200, y: defaultY },
                    data: {},
                };

                setNodes((currentNodes) => [...currentNodes, startNode, methodsNode]);

                // Create edge from start to methodsNode
                setEdges((eds) => [
                    ...eds,
                    {
                        id: `edge-${startNodeId}-${methodsNodeId}`,
                        source: startNodeId,
                        target: methodsNodeId,
                        type: "customEdge",
                        markerEnd: { type: MarkerType.ArrowClosed, color: "#0706254D" },
                    },
                ]);

                handleCloseCanvasMenu();
            },
            [canvasMenuState.flowPosition, nodes, reactFlowInstance, handleCloseCanvasMenu, ccEntity]
        );

        // Handle edge right-click context menu
        const handleEdgeContextMenu = useCallback((event: React.MouseEvent, edge: Edge) => {
            event.preventDefault();
            setEdgeMenuState({
                open: true,
                edgeId: edge.id,
                screenPosition: { x: event.clientX, y: event.clientY },
            });
        }, []);

        // Close edge context menu
        const handleCloseEdgeMenu = useCallback(() => {
            setEdgeMenuState({
                open: false,
                edgeId: null,
                screenPosition: null,
            });
        }, []);

        // Handle edge context menu actions
        const handleEdgeMenuSettings = useCallback(() => {
            if (!edgeMenuState.edgeId) return;
            const edge = edges.find((e) => e.id === edgeMenuState.edgeId);
            if (edge) {
                setSelectedEdge({
                    id: edge.id,
                    source: edge.source,
                    target: edge.target,
                    data: edge.data as Record<string, unknown> | undefined,
                });
                showDrawer(DrawerId.Condition, {
                    [getCCDrawerSelectionParam(DrawerId.Condition)]: getEdgeSelectionId(edge),
                });
            }
            handleCloseEdgeMenu();
        }, [edgeMenuState.edgeId, edges, setSelectedEdge, showDrawer, handleCloseEdgeMenu]);

        const handleEdgeMenuDelete = useCallback(() => {
            if (!edgeMenuState.edgeId) return;
            setEdges((currentEdges) => currentEdges.filter((e) => e.id !== edgeMenuState.edgeId));
            handleCloseEdgeMenu();
        }, [edgeMenuState.edgeId, handleCloseEdgeMenu]);

        // Handle node type selection from canvas context menu (unconnected node)
        const handleCanvasAddNodeSelect = useCallback(
            (nodeType: NodeType) => {
                if (!canvasMenuState.flowPosition) return;

                // Generate ID for new node (use flow 0 for unconnected nodes)
                const newNodeId = getNextFlowPositionId(nodes, 0);

                const newNode: Node = {
                    id: newNodeId,
                    type: nodeType,
                    position: canvasMenuState.flowPosition,
                    data: {
                        formData: getDefaultFormData(nodeType),
                    },
                };

                setNodes((currentNodes) => [...currentNodes, newNode]);
                handleCloseCanvasMenu();
            },
            [canvasMenuState.flowPosition, nodes, handleCloseCanvasMenu]
        );

        // Handle node type selection from Plus button dialog
        const handleAddNodeTypeSelect = useCallback(
            (nodeType: NodeType) => {
                const { sourceNodeId, side } = addNodeDialogState;
                if (!sourceNodeId || !side) return;

                const sourceNode = nodes.find((n) => n.id === sourceNodeId);
                if (!sourceNode) return;

                // Get flow number from source node and generate next position ID
                const flowNumber = getFlowNumber(sourceNodeId);
                const newNodeId = getNextFlowPositionId(nodes, flowNumber);
                const offset = side === "right" ? 200 : -200;

                // Calculate Y offset to stack multiple outputs vertically (avoid overlay)
                let yOffset = 0;
                if (side === "right") {
                    const existingOutputs = edges.filter((e) => e.source === sourceNodeId).length;
                    yOffset = existingOutputs * 100;
                }

                const newNode: Node = {
                    id: newNodeId,
                    type: nodeType,
                    position: {
                        x: sourceNode.position.x + offset,
                        y: sourceNode.position.y + yOffset,
                    },
                    data: {
                        formData: getDefaultFormData(nodeType),
                    },
                };

                setNodes((currentNodes) => [...currentNodes, newNode]);

                // Create edge based on side
                const newEdge: Edge =
                    side === "right"
                        ? {
                              id: `edge-${sourceNodeId}-${newNodeId}`,
                              source: sourceNodeId,
                              target: newNodeId,
                              type: "customEdge",
                              markerEnd: { type: MarkerType.ArrowClosed, color: "#0706254D" },
                          }
                        : {
                              id: `edge-${newNodeId}-${sourceNodeId}`,
                              source: newNodeId,
                              target: sourceNodeId,
                              type: "customEdge",
                              markerEnd: { type: MarkerType.ArrowClosed, color: "#0706254D" },
                          };

                setEdges((currentEdges) => [...currentEdges, newEdge]);
                handleCloseAddNodeDialog();
            },
            [addNodeDialogState, nodes, edges, handleCloseAddNodeDialog]
        );

        // Delete a node and its connected edges (connected nodes become disconnected)
        const handleDeleteNode = useCallback((nodeId: string) => {
            // Find connected methods node that should also be removed (e.g., when deleting start node)
            const connectedEdge = edgesRef.current.find((edge) => edge.source === nodeId);
            const methodsNodeToRemove =
                connectedEdge &&
                nodesRef.current.find((n) => n.id === connectedEdge.target && n.type === NodeType.Methods);

            // Delete the node, connected methods node (if any), and their edges
            const idsToRemove = methodsNodeToRemove ? [nodeId, methodsNodeToRemove.id] : [nodeId];

            setNodes((currentNodes) => currentNodes.filter((n) => !idsToRemove.includes(n.id)));
            setEdges((currentEdges) =>
                currentEdges.filter((e) => !idsToRemove.includes(e.source) && !idsToRemove.includes(e.target))
            );
        }, []);

        // Handler for deleting an edge
        const handleDeleteEdge = useCallback((edgeId: string) => {
            setEdges((currentEdges) => currentEdges.filter((edge) => edge.id !== edgeId));
        }, []);

        // Handler for clicking the wrench icon on an edge
        const handleEdgeWrenchClick = useCallback(
            (edgeId: string) => {
                const edge = edges.find((e) => e.id === edgeId);
                if (edge) {
                    setSelectedEdge({
                        id: edge.id,
                        source: edge.source,
                        target: edge.target,
                        data: edge.data as Record<string, unknown> | undefined,
                    });
                    showDrawer(DrawerId.Condition, {
                        [getCCDrawerSelectionParam(DrawerId.Condition)]: getEdgeSelectionId(edge),
                    });
                }
            },
            [edges, setSelectedEdge, showDrawer]
        );

        // Handler for clicking anywhere on the edge/connection line — opens the same
        // Condition drawer as the wrench icon, for edges that show a wrench.
        const handleEdgeClick = useCallback(
            (_event: React.MouseEvent, edge: Edge) => {
                if (!(edge.data as CustomEdgeData | undefined)?.showWrench) return;
                handleEdgeWrenchClick(edge.id);
            },
            [handleEdgeWrenchClick]
        );

        // Hovering anywhere on the edge/connection line (including its wide invisible
        // hit-area) highlights both the line and its wrench icon together.
        const handleEdgeMouseEnter = useCallback((_event: React.MouseEvent, edge: Edge) => {
            setHoveredEdgeId(edge.id);
        }, []);

        const handleEdgeMouseLeave = useCallback(() => {
            setHoveredEdgeId(null);
        }, []);

        // Add handlers to nodes for rendering
        const nodesWithHandlers = useMemo(() => {
            const isMethodsNode = (type: string) => type === NodeType.Methods;
            const isMirrorNode = (type: string) => type === NodeType.Mirror;
            const isStartNode = (type: string) => type === NodeType.Start;
            const isEndNode = (type: string) => type === NodeType.End;

            // Calculate sort order for all nodes
            const sortOrderMap = calculateSortOrder(nodes, edges);

            return nodes.map((node) => {
                const nodeType = node.type ?? "";
                const formData = (node.data as Record<string, unknown>)?.formData as NodeFormData | undefined;

                // For mirror nodes, get the sort order of the mirrored element
                let mirroredNodeSortOrder: number | undefined;
                if (isMirrorNode(nodeType) && formData && "mirrorElement" in formData && formData.mirrorElement) {
                    mirroredNodeSortOrder = sortOrderMap.get(formData.mirrorElement);
                }

                // Check connections for this node
                const hasLeftConnection = edges.some((e) => e.target === node.id);

                // Determine if Plus buttons should be shown
                // - Methods nodes don't get Plus buttons (they ARE the placeholder)
                // - End nodes: no Plus buttons (it's a terminal node)
                // - Start nodes: only right side, always show (multiple routes with conditions)
                // - All other nodes: always show Plus on right (multiple routes with conditions),
                //   show Plus on left only if no incoming connection
                let showPlusLeft = false;
                let showPlusRight = false;

                if (!isMethodsNode(nodeType) && !isEndNode(nodeType)) {
                    if (isStartNode(nodeType)) {
                        showPlusRight = true; // Start nodes can have multiple routes
                    } else {
                        showPlusLeft = !hasLeftConnection;
                        showPlusRight = true; // All nodes can have multiple output routes with conditions
                    }
                }

                // Create the onClick handler for reuse in context menu
                const handleOpenDrawer = () => {
                    const drawerId = NODE_DRAWER_MAP[nodeType];
                    if (drawerId) {
                        setSelectedNode({
                            id: node.id,
                            type: nodeType,
                            formData,
                        });
                        showDrawer(drawerId, {
                            [getCCDrawerSelectionParam(drawerId)]: getNodeSelectionId(node),
                        });
                    }
                };

                return {
                    ...node,
                    data: {
                        ...node.data,
                        sortOrder: sortOrderMap.get(node.id),
                        mirroredNodeSortOrder,
                        showPlusLeft,
                        showPlusRight,
                        onAddNode: (side: "left" | "right") => handleOpenAddNodeDialog(node.id, side),
                        onClick: handleOpenDrawer,
                        onDelete: handleDeleteNode,
                        // For methodsNode: provide callback to transform into selected type
                        onSelectType: isMethodsNode(nodeType)
                            ? (newType: NodeType) => handleTransformNode(node.id, newType)
                            : undefined,
                        // Context menu handlers
                        onContextMenuSettings: handleOpenDrawer,
                        onContextMenuRename: handleOpenDrawer, // Opens drawer where title can be edited
                        onContextMenuDelete: () => handleDeleteNode(node.id),
                        disableContextMenu: isMethodsNode(nodeType), // Disable context menu for empty placeholder nodes
                    },
                };
            });
        }, [nodes, edges, showDrawer, handleDeleteNode, setSelectedNode, handleTransformNode, handleOpenAddNodeDialog]);

        // Add handlers to edges for rendering
        const edgesWithHandlers = useMemo(() => {
            return edges.map((edge) => {
                // Check source node type - show wrench for conditions on all edges
                // except edges from Methods nodes (placeholder)
                const sourceNode = nodes.find((n) => n.id === edge.source);
                const isSourceMethods = sourceNode?.type === NodeType.Methods;
                const showWrench = !isSourceMethods;

                return {
                    ...edge,
                    data: {
                        ...edge.data,
                        onWrenchClick: handleEdgeWrenchClick,
                        onDelete: handleDeleteEdge,
                        showWrench,
                        isHovered: edge.id === hoveredEdgeId,
                    },
                };
            });
        }, [edges, nodes, handleEdgeWrenchClick, handleDeleteEdge, hoveredEdgeId]);

        // Node options for mirror form (and other forms that need to select nodes)
        const nodeOptions: DiagramNodeOption[] = useMemo(() => {
            const sortOrderMap = calculateSortOrder(nodes, edges);

            // Calculate actual flow numbers based on graph connectivity (not node IDs)
            // Build bidirectional adjacency for traversal
            const adjacency = new Map<string, string[]>();
            edges.forEach((edge) => {
                const forward = adjacency.get(edge.source) || [];
                forward.push(edge.target);
                adjacency.set(edge.source, forward);
                const backward = adjacency.get(edge.target) || [];
                backward.push(edge.source);
                adjacency.set(edge.target, backward);
            });

            // Find which flow each node belongs to via BFS from start nodes
            const nodeFlowMap = new Map<string, number>();
            const nodeFlowTypeMap = new Map<string, string>();
            const startNodes = nodes.filter((n) => n.type === NodeType.Start);

            startNodes.forEach((startNode, index) => {
                const flowNumber = index + 1;
                // Get flow type from start node's formData
                const startFormData = (startNode.data as Record<string, unknown>)?.formData as
                    | Record<string, unknown>
                    | undefined;
                const flowType = (startFormData?.flow as string) || "";

                const queue = [startNode.id];
                const visited = new Set<string>();

                while (queue.length > 0) {
                    const nodeId = queue.shift();
                    if (!nodeId || visited.has(nodeId)) continue;
                    visited.add(nodeId);

                    // Only set flow if not already assigned (first flow wins for shared nodes)
                    if (!nodeFlowMap.has(nodeId)) {
                        nodeFlowMap.set(nodeId, flowNumber);
                        nodeFlowTypeMap.set(nodeId, flowType);
                    }

                    const neighbors = adjacency.get(nodeId) || [];
                    neighbors.forEach((neighbor) => {
                        if (!visited.has(neighbor)) {
                            queue.push(neighbor);
                        }
                    });
                }
            });

            return nodes
                .filter((node) => node.type !== NodeType.Methods) // Exclude empty placeholder nodes
                .map((node) => ({
                    id: node.id,
                    type: node.type ?? "unknown",
                    sortOrder: sortOrderMap.get(node.id),
                    label: (node.data as Record<string, unknown>)?.formData
                        ? (((node.data as Record<string, unknown>).formData as Record<string, unknown>)
                              ?.title as string)
                        : undefined,
                    flowNumber: nodeFlowMap.get(node.id) ?? 0, // 0 = orphan/not connected
                    flowType: nodeFlowTypeMap.get(node.id),
                }));
        }, [nodes, edges]);

        // Extract clean state for saving (remove runtime handlers)
        const getCleanState = useCallback((): DiagramState => {
            const cleanNodes: DiagramNode[] = nodes.map((node) => {
                const data: Record<string, unknown> = {};
                if (node.data && typeof node.data === "object") {
                    if ("method" in node.data && node.data.method) data.method = node.data.method;
                    if ("label" in node.data && node.data.label) data.label = node.data.label;
                    // Include form data for persistence
                    if ("formData" in node.data && node.data.formData) data.formData = node.data.formData;
                    // Include beId for API persistence (maps to BE step UUID)
                    if ("beId" in node.data && node.data.beId) data.beId = node.data.beId;
                }
                return {
                    id: node.id,
                    type: node.type ?? "unknown",
                    position: node.position,
                    data,
                };
            });

            const cleanEdges: DiagramEdge[] = edges.map(toCleanDiagramEdge);

            return { nodes: cleanNodes, edges: cleanEdges, notes: notesContent || undefined };
        }, [nodes, edges, notesContent]);

        // Handle applying blueprint (import)
        const handleApplyBlueprint = useCallback(
            (data: { nodes: DiagramNode[]; edges: DiagramEdge[] }) => {
                isApplyingBlueprintRef.current = true;
                setSelectedNode(null);
                setSelectedEdge(null);
                const cleanNodes = data.nodes.map((node) => {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const { beId: _beId, ...cleanData } = (node.data || {}) as Record<string, unknown>;
                    return { ...node, data: cleanData };
                });
                const cleanEdges = data.edges.map((edge) => {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const { beId: _beId, ...cleanData } = (edge.data || {}) as Record<string, unknown>;
                    return { ...edge, data: cleanData };
                });
                setNodes(toReactFlowNodes(cleanNodes));
                setEdges(toReactFlowEdges(cleanEdges));
                // Clear after one tick so the restore effect sees the flag before nodes/edges settle
                requestAnimationFrame(() => {
                    isApplyingBlueprintRef.current = false;
                });
            },
            [setSelectedEdge, setSelectedNode, setEdges, setNodes]
        );

        // Memoized blueprint value for drawer (only recompute when nodes/edges actually change)
        const blueprintValue = useMemo(() => getCleanState(), [getCleanState]);

        // Expose ref methods
        useImperativeHandle(
            ref,
            () => ({
                getState: getCleanState,
                setNodes: (newNodes: DiagramNode[]) => {
                    setNodes(toReactFlowNodes(newNodes));
                },
                setEdges: (newEdges: DiagramEdge[]) => {
                    setEdges(toReactFlowEdges(newEdges));
                },
                // History methods
                undo: handleUndo,
                redo: handleRedo,
                canUndo,
                canRedo,
                // Save state
                isDirty,
                markAsSaved,
            }),
            [getCleanState, handleUndo, handleRedo, canUndo, canRedo, isDirty, markAsSaved, nodes]
        );

        // Handle node/edge changes from ReactFlow
        // When a node with connected methodsNode is dragged, move the methodsNode too
        // When a start node is deleted, also delete connected methodsNode
        const handleNodesChange = useCallback((changes: Parameters<typeof applyNodeChanges>[0]) => {
            // Check for remove changes - need to also remove connected methods nodes
            const removeChanges = changes.filter(
                (change): change is { type: "remove"; id: string } => change.type === "remove"
            );

            // Find methods nodes that should be removed because their parent node is being removed
            const additionalRemoveIds: string[] = [];
            removeChanges.forEach((change) => {
                // Find edges where this node is the source (using ref for latest edges)
                const connectedEdge = edgesRef.current.find((edge) => edge.source === change.id);
                if (connectedEdge) {
                    // Check if target is a methods node (using ref for latest nodes)
                    const targetNode = nodesRef.current.find((n) => n.id === connectedEdge.target);
                    if (targetNode?.type === NodeType.Methods) {
                        additionalRemoveIds.push(targetNode.id);
                    }
                }
            });

            // Add additional remove changes for connected methods nodes
            const augmentedChanges = [
                ...changes,
                ...additionalRemoveIds.map((id) => ({ type: "remove" as const, id })),
            ];

            // Also remove edges connected to the methods nodes being removed
            if (additionalRemoveIds.length > 0) {
                setEdges((currentEdges) =>
                    currentEdges.filter(
                        (edge) =>
                            !additionalRemoveIds.includes(edge.source) && !additionalRemoveIds.includes(edge.target)
                    )
                );
            }

            setNodes((currentNodes) => {
                // Deep clone nodes before applying changes - Recoil state is immutable but applyNodeChanges mutates
                const clonedNodes = structuredClone(currentNodes);
                // First, apply the original changes (with additional removes)
                let updatedNodes = applyNodeChanges(augmentedChanges, clonedNodes);

                // Check for position changes (dragging)
                const positionChanges = changes.filter(
                    (
                        change
                    ): change is {
                        type: "position";
                        id: string;
                        position?: { x: number; y: number };
                        dragging?: boolean;
                    } => change.type === "position" && change.position !== undefined
                );

                if (positionChanges.length > 0) {
                    // For each dragged node, check if it has a connected methodsNode
                    positionChanges.forEach((change) => {
                        const draggedNodeId = change.id;
                        const draggedNode = currentNodes.find((n) => n.id === draggedNodeId);

                        // Skip if the dragged node is itself a methodsNode
                        if (draggedNode?.type === NodeType.Methods) return;

                        // Find edges where this node is the source and target is a methodsNode (using ref)
                        const connectedMethodsEdge = edgesRef.current.find((edge) => edge.source === draggedNodeId);

                        if (connectedMethodsEdge) {
                            const targetNode = currentNodes.find((n) => n.id === connectedMethodsEdge.target);

                            // Only move if target is a methodsNode
                            if (targetNode?.type === NodeType.Methods && change.position && draggedNode) {
                                // Calculate the delta from the original position
                                const deltaX = change.position.x - draggedNode.position.x;
                                const deltaY = change.position.y - draggedNode.position.y;

                                // Apply the same delta to the methodsNode
                                updatedNodes = updatedNodes.map((node) =>
                                    node.id === targetNode.id
                                        ? {
                                              ...node,
                                              position: {
                                                  x: node.position.x + deltaX,
                                                  y: node.position.y + deltaY,
                                              },
                                          }
                                        : node
                                );
                            }
                        }
                    });
                }

                return updatedNodes;
            });
        }, []);

        const handleEdgesChange = useCallback((changes: Parameters<typeof applyEdgeChanges>[0]) => {
            // Deep clone edges before applying changes - Recoil state is immutable but applyEdgeChanges mutates
            setEdges((eds) => applyEdgeChanges(changes, structuredClone(eds)));
        }, []);

        // Track if reconnection was successful
        const reconnectSuccessRef = useRef(false);

        // Handle edge reconnection (dragging edge to different node)
        const handleReconnect = useCallback(
            (oldEdge: Edge, newConnection: Connection) => {
                if (!newConnection.source || !newConnection.target) return;

                // Don't allow self-connections
                if (newConnection.source === newConnection.target) return;

                // Check flow compatibility using actual connectivity (not ID-based)
                const sourceNode = nodeOptions.find((n) => n.id === newConnection.source);
                const targetNode = nodeOptions.find((n) => n.id === newConnection.target);
                const sourceFlow = sourceNode?.flowNumber ?? 0;
                const targetFlow = targetNode?.flowNumber ?? 0;

                // Allow if either node is orphan (flow 0) - it will join the other's flow
                // Allow if same flow
                // Block if both have actual flows but they're different
                if (sourceFlow !== targetFlow && sourceFlow !== 0 && targetFlow !== 0) {
                    message.error(t("connection.cross_flow_error"));
                    return;
                }

                reconnectSuccessRef.current = true;

                // oldEdge comes from ReactFlow's rendered edges (edgesWithHandlers), whose `.data`
                // carries onWrenchClick/onDelete callbacks and UI-only flags — reconnectEdge merges
                // oldEdge's fields into the result, so passing it through unstripped would write
                // those non-serializable functions into persisted Recoil state, permanently
                // breaking every later structuredClone() call on the edges array (crash on any
                // subsequent drag/reconnect). Mirrors the same stripping done when persisting edges
                // elsewhere in this file (e.g. toReactFlowEdges/edge-to-DiagramEdge mapping above).
                const cleanOldEdge = {
                    ...oldEdge,
                    data: sanitizeDiagramEdgeData(oldEdge.data),
                };

                // Deep clone edges before reconnect - Recoil state is immutable
                setEdges((eds) => reconnectEdge(cleanOldEdge, newConnection, structuredClone(eds)));
            },
            [t, nodeOptions]
        );

        // Handle reconnect start - reset success flag
        const handleReconnectStart = useCallback(() => {
            reconnectSuccessRef.current = false;
        }, []);

        // Handle reconnect end - if dropped on empty space, delete the edge
        const handleReconnectEnd = useCallback((_event: MouseEvent | TouchEvent, edge: Edge) => {
            if (!reconnectSuccessRef.current) {
                // Edge was dropped on empty space - delete it
                setEdges((eds) => eds.filter((e) => e.id !== edge.id));
            }
        }, []);

        // Handle new connections (dragging from source handle to target handle)
        const handleConnect = useCallback(
            (connection: Connection) => {
                if (!connection.source || !connection.target) return;

                // Don't allow self-connections
                if (connection.source === connection.target) return;

                // Check flow compatibility using actual connectivity (not ID-based)
                const sourceNode = nodeOptions.find((n) => n.id === connection.source);
                const targetNode = nodeOptions.find((n) => n.id === connection.target);
                const sourceFlow = sourceNode?.flowNumber ?? 0;
                const targetFlow = targetNode?.flowNumber ?? 0;

                // Allow if either node is orphan (flow 0) - it will join the other's flow
                // Allow if same flow
                // Block if both have actual flows but they're different
                if (sourceFlow !== targetFlow && sourceFlow !== 0 && targetFlow !== 0) {
                    message.error(t("connection.cross_flow_error"));
                    return;
                }

                // Check if edge already exists
                const edgeExists = edges.some((e) => e.source === connection.source && e.target === connection.target);
                if (edgeExists) return;

                const newEdge: Edge = {
                    id: `edge-${connection.source}-${connection.target}`,
                    source: connection.source,
                    target: connection.target,
                    type: "customEdge",
                    markerEnd: { type: MarkerType.ArrowClosed, color: "#0706254D" },
                };
                setEdges((currentEdges) => [...currentEdges, newEdge]);
            },
            [edges, t, nodeOptions]
        );

        return (
            <Fragment>
                {/* Drawers - visibility handled by Drawer component based on URL params */}
                <CCStartNodesProvider existingFlowTypes={existingFlowTypes} totalStarts={existingFlowTypes.length}>
                    <DiagramNodesProvider nodeOptions={nodeOptions}>
                        {/* Start/Control */}
                        <StartDrawer />
                        <EndDrawer />
                        <WaitDrawer />
                        <RepeaterDrawer />
                        <BreakerDrawer />
                        {/* Data */}
                        <ModifyFieldDrawer />
                        <SetDrawer />
                        <MirrorDrawer />
                        {/* HTTP */}
                        <HttpDrawer />
                        <HttpMethodDrawer />
                        {/* Communication */}
                        <WebhookDrawer />
                        <SubScenarioDrawer />
                        {/* Edge/Connection */}
                        <ConditionDrawer />
                    </DiagramNodesProvider>
                </CCStartNodesProvider>

                {/* Notes Drawer */}
                <NotesDrawer
                    open={notesDrawerOpen}
                    onClose={() => setNotesDrawerOpen(false)}
                    value={notesContent}
                    onChange={setNotesContent}
                />

                {/* Blueprint Drawer */}
                <BlueprintDrawer
                    open={blueprintDrawerOpen}
                    onClose={() => setBlueprintDrawerOpen(false)}
                    value={blueprintValue}
                    onApply={handleApplyBlueprint}
                />

                {/* Add Node Menu - shown when Plus button on a node is clicked */}
                {addNodeDialogState.open && (
                    <div className="fixed inset-0 z-50" onClick={handleCloseAddNodeDialog}>
                        <div
                            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg p-0"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <NodePopover.Menu onClose={handleCloseAddNodeDialog} withSearch>
                                {getMenuNodesByCategory().map(({ category, nodes: categoryNodes }) => (
                                    <NodePopover.MenuSection
                                        key={category}
                                        title={t(`enums.node_category.${category}`)}
                                        category={category}
                                    >
                                        {categoryNodes.map(({ type, entry }) => (
                                            <NodePopover.MenuSectionItem
                                                key={type}
                                                title={t(entry.title)}
                                                color={entry.color}
                                                icon={entry.icon}
                                                onClick={() => handleAddNodeTypeSelect(type)}
                                            />
                                        ))}
                                    </NodePopover.MenuSection>
                                ))}
                            </NodePopover.Menu>
                        </div>
                    </div>
                )}

                {/* Canvas Context Menu - shown when right-clicking on empty canvas space */}
                {canvasMenuState.open && canvasMenuState.screenPosition && (
                    <div className="fixed inset-0 z-50" onClick={handleCloseCanvasMenu}>
                        <div
                            className="absolute bg-white rounded-lg shadow-lg p-0"
                            style={{
                                left: canvasMenuState.screenPosition.x,
                                top: canvasMenuState.screenPosition.y,
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <NodePopover.Menu onClose={handleCloseCanvasMenu} withSearch>
                                {availableStartFlowTypes.length > 0 && (
                                    <NodePopover.MenuSection title={t("enums.node_category.start_nodes")}>
                                        {START_NODE_MENU_CONFIG.filter((node) =>
                                            availableStartFlowTypes.includes(node.type)
                                        ).map((node) => (
                                            <NodePopover.MenuSectionItem
                                                key={node.type}
                                                title={t(node.labelKey)}
                                                color={node.color}
                                                icon={node.icon}
                                                onClick={() => handleStartNodeTypeSelect(node.type)}
                                            />
                                        ))}
                                    </NodePopover.MenuSection>
                                )}
                                {getMenuNodesByCategory().map(({ category, nodes: categoryNodes }) => (
                                    <NodePopover.MenuSection
                                        key={category}
                                        title={t(`enums.node_category.${category}`)}
                                        category={category}
                                    >
                                        {categoryNodes.map(({ type, entry }) => (
                                            <NodePopover.MenuSectionItem
                                                key={type}
                                                title={t(entry.title)}
                                                color={entry.color}
                                                icon={entry.icon}
                                                onClick={() => handleCanvasAddNodeSelect(type)}
                                            />
                                        ))}
                                    </NodePopover.MenuSection>
                                ))}
                            </NodePopover.Menu>
                        </div>
                    </div>
                )}

                {/* Edge Context Menu - shown when right-clicking on edge/arrow */}
                {edgeMenuState.open && edgeMenuState.screenPosition && (
                    <div className="fixed inset-0 z-50" onClick={handleCloseEdgeMenu}>
                        <Dropdown
                            open
                            menu={{
                                items: [
                                    {
                                        key: "settings",
                                        icon: <SettingOutlined />,
                                        label: t("context_menu.settings"),
                                        onClick: handleEdgeMenuSettings,
                                    },
                                    { type: "divider" },
                                    {
                                        key: "delete",
                                        icon: <DeleteOutlined />,
                                        label: t("context_menu.delete"),
                                        danger: true,
                                        onClick: handleEdgeMenuDelete,
                                    },
                                ] as MenuProps["items"],
                            }}
                        >
                            <div
                                style={{
                                    position: "fixed",
                                    left: edgeMenuState.screenPosition.x,
                                    top: edgeMenuState.screenPosition.y,
                                    width: 1,
                                    height: 1,
                                }}
                            />
                        </Dropdown>
                    </div>
                )}

                <div ref={containerRef} style={{ height, width: "100%" }}>
                    <ReactFlow
                        nodes={nodesWithHandlers}
                        edges={edgesWithHandlers}
                        nodeTypes={nodeTypes}
                        edgeTypes={edgeTypes}
                        onNodesChange={handleNodesChange}
                        onEdgesChange={handleEdgesChange}
                        onReconnectStart={handleReconnectStart}
                        onReconnect={handleReconnect}
                        onReconnectEnd={handleReconnectEnd}
                        onConnect={handleConnect}
                        onPaneContextMenu={handlePaneContextMenu}
                        onEdgeContextMenu={handleEdgeContextMenu}
                        onEdgeClick={handleEdgeClick}
                        onEdgeMouseEnter={handleEdgeMouseEnter}
                        onEdgeMouseLeave={handleEdgeMouseLeave}
                        fitView
                        proOptions={{ hideAttribution: true }}
                    >
                        {showControls && (
                            <>
                                <Panel position="bottom-left">
                                    <div className="flex gap-1">
                                        {/* TODO: Run button - not implemented yet
                                        <Button type="primary" icon={<PlayCircleOutlined />}>
                                            {t("controls.run")}
                                        </Button>
                                        */}
                                        {/* TODO: Share button - not implemented yet
                                        <Button type="default" icon={<ShareAltOutlined />}>
                                            {t("controls.share")}
                                        </Button>
                                        */}
                                        <Button
                                            type="default"
                                            icon={<CodeOutlined />}
                                            onClick={() => setBlueprintDrawerOpen(true)}
                                        >
                                            {t("controls.blueprint")}
                                        </Button>
                                        {/* TODO: Notes drawer - not implemented yet
                                        <Button
                                            type="default"
                                            icon={<FontSizeOutlined />}
                                            onClick={() => setNotesDrawerOpen(true)}
                                            title={t("controls.notes")}
                                        />
                                        */}
                                        <Button
                                            type="primary"
                                            icon={<ArrowLeftOutlined />}
                                            onClick={handleUndo}
                                            disabled={!canUndo}
                                            title="Undo (Ctrl+Z)"
                                        />
                                        <Button
                                            type="primary"
                                            icon={<ArrowRightOutlined />}
                                            onClick={handleRedo}
                                            disabled={!canRedo}
                                            title="Redo (Ctrl+Shift+Z)"
                                        />
                                        <Button
                                            type="primary"
                                            icon={<PlusOutlined />}
                                            onClick={handleOpenToolbarMenu}
                                            aria-label={t("controls.add_node")}
                                            title={t("controls.add_node")}
                                        />
                                        {onSave && (
                                            <Button
                                                type="primary"
                                                icon={isSaving ? <LoadingOutlined spin /> : <SaveOutlined />}
                                                onClick={onSave}
                                                disabled={!isDirty || isSaving}
                                                title={isSaving ? t("controls.saving") : t("controls.save")}
                                            >
                                                {isSaving && t("controls.saving")}
                                            </Button>
                                        )}
                                    </div>
                                </Panel>
                                <Panel position="bottom-right">
                                    <div className="flex gap-1 items-center">
                                        <Button
                                            type="primary"
                                            icon={<ZoomOutOutlined />}
                                            onClick={handleZoomOut}
                                            title={t("controls.zoom_out")}
                                        />
                                        <InputNumber
                                            value={Math.round(viewport.zoom * 100)}
                                            onChange={handleZoomChange}
                                            min={10}
                                            max={200}
                                            formatter={(value) => `${value}%`}
                                            parser={(value) => Number(value?.replace("%", "") || 100)}
                                            style={{ width: 80 }}
                                            controls={false}
                                        />
                                        <Button
                                            type="primary"
                                            icon={<ZoomInOutlined />}
                                            onClick={handleZoomIn}
                                            title={t("controls.zoom_in")}
                                        />
                                    </div>
                                </Panel>
                            </>
                        )}
                        <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
                    </ReactFlow>
                </div>
            </Fragment>
        );
    }
);

DiagramContent.displayName = "DiagramContent";

// Main component - must be used inside ReactFlowProvider
export const ConnectionCreatorDiagram = forwardRef<ConnectionCreatorDiagramRef, ConnectionCreatorDiagramProps>(
    (props, ref) => {
        return <DiagramContent ref={ref} {...props} />;
    }
);

ConnectionCreatorDiagram.displayName = "ConnectionCreatorDiagram";
